import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import apiClient from "../../services/apiClient";
import SEO from "../../components/SEO";
import {
  getApproachabilityLabel,
  getPriorityLabel,
} from "../constants/prospectLabels";

const STATUS_FLOW = [
  { key: "pending_review", label: "Brouillon", color: "#9E9E9E" },
  { key: "entity_found", label: "Trouvé", color: "#90CAF9" },
  { key: "raw_lead", label: "Brut", color: "#CE93D8" },
  { key: "to_enrich", label: "À enrichir", color: "#FFB74D" },
  { key: "qualified", label: "Qualifié", color: "#81C784" },
  { key: "priority", label: "Prioritaire", color: "#4CAF50" },
  { key: "contacted", label: "Contacté", color: "#42A5F5" },
  { key: "excluded", label: "Exclu", color: "#EF5350" },
  { key: "archived", label: "Archivé", color: "#BDBDBD" },
];
const ENRICH_SOURCES = [
  { key: "google", label: "Google Places" },
  { key: "pappers", label: "Pappers (données entreprise)" },
  { key: "pages_jaunes", label: "PagesJaunes" },
  { key: "website_scan", label: "Scanner le site web (emails, CMS, SEO)" },
];

function ScoreBar({ label, value, max, tip }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const color = pct >= 70 ? "success.main" : pct >= 40 ? "warning.main" : "error.main";
  return (
    <Tooltip title={tip || ""} placement="right" arrow>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, cursor: tip ? "help" : "default" }}>
        <Typography variant="caption" sx={{ width: 100, flexShrink: 0 }}>{label}</Typography>
        <Box sx={{ flex: 1, height: 8, bgcolor: "grey.200", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, borderRadius: 1 }} />
        </Box>
        <Typography variant="caption" sx={{ width: 45, textAlign: "right" }}>{value}/{max}</Typography>
      </Box>
    </Tooltip>
  );
}

function SourceBadge({ type }) {
  const colors = {
    google_places: "#4285F4",
    google: "#4285F4",
    pages_jaunes: "#FFD700",
    pj_scraping: "#FFD700",
    pappers: "#6C63FF",
    website_scan: "#00C853",
    manual: "#9E9E9E",
    admin_search: "#FF7043",
  };
  return (
    <Chip
      size="small"
      label={type}
      sx={{
        bgcolor: colors[type] || "#9E9E9E",
        color: "#fff",
        fontSize: "0.65rem",
        height: 18,
        mr: 0.5,
        mb: 0.25,
      }}
    />
  );
}

function RecommendedActions({ prospect }) {
  const actions = [];
  const pc = prospect.contacts?.[0];

  if (!prospect.website_url) {
    actions.push({ priority: "high", text: "Pas de site web trouvé → chercher manuellement ou via Google" });
  } else {
    const webSignals = (prospect.signals || []).filter((s) => s.signal_type === "website_scan");
    if (webSignals.length === 0) {
      actions.push({ priority: "high", text: "Lancer un scan du site web pour extraire emails, CMS, problèmes SEO" });
    }
  }

  if (!pc?.email) {
    actions.push({ priority: "high", text: "Email manquant → scanner le site web ou chercher sur LinkedIn/Pappers" });
  }

  if (!pc?.phone) {
    actions.push({ priority: "medium", text: "Téléphone manquant → enrichir via Google ou PagesJaunes" });
  }

  const hasPappers = (prospect.sources || []).some((s) => s.source_type === "pappers");
  if (!hasPappers) {
    actions.push({ priority: "medium", text: "Enrichir via Pappers (CA, effectifs, données légales)" });
  }

  if (!prospect.last_scored_at) {
    actions.push({ priority: "medium", text: "Recalculer le score après enrichissement" });
  }

  const cmsSignal = (prospect.signals || []).find((s) => s.label?.startsWith("CMS détecté:"));
  if (cmsSignal && ["Wix", "Jimdo", "Weebly"].some((c) => cmsSignal.label.includes(c))) {
    actions.push({ priority: "high", text: `${cmsSignal.label} → fort potentiel de migration/refonte` });
  }

  if (prospect.global_score >= 65 && pc?.email) {
    actions.push({ priority: "low", text: "Prospect qualifié avec email → prêt pour prise de contact" });
  }

  if (actions.length === 0) {
    actions.push({ priority: "low", text: "Prospect bien renseigné. Vérifier manuellement et avancer dans le pipeline." });
  }

  const priorityColor = { high: "error.main", medium: "warning.main", low: "success.main" };

  return (
    <Box>
      {actions.map((a, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priorityColor[a.priority], mt: 0.7, flexShrink: 0 }} />
          <Typography variant="caption">{a.text}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function AdminProspectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [patch, setPatch] = useState({
    exclusion_reason: "",
    primary_problem: "",
    primary_angle: "",
    recommended_channel: "",
  });

  const load = useCallback(async () => {
    try {
      const data = await apiClient.adminProspectById(id);
      setProspect(data);
      setPatch({
        exclusion_reason: data.exclusion_reason || "",
        primary_problem: data.primary_problem || "",
        primary_angle: data.primary_angle || "",
        recommended_channel: data.recommended_channel || "",
      });
    } catch (error) {
      setErrorMessage(error.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const withAction = async (key, fn) => {
    setBusyAction(key);
    setErrorMessage("");
    setMessage("");
    try { await fn(); } catch (error) { setErrorMessage(error.message || "Erreur"); } finally { setBusyAction(""); }
  };

  const save = () => withAction("save", async () => {
    const data = await apiClient.adminProspectPatch(id, patch);
    setProspect(data);
    setMessage("Sauvegardé.");
  });

  const recalculate = () => withAction("recalculate", async () => {
    const data = await apiClient.adminProspectRecalculate(id);
    setProspect(data);
    setMessage("Score recalculé.");
  });

  const runEnrichment = (source) => withAction(source, async () => {
    await apiClient.adminProspectEnrich(id, source);
    setMessage(`Enrichissement ${source} terminé.`);
    await load();
  });

  const enrichAll = () => withAction("enrich_all", async () => {
    await apiClient.adminProspectEnrichAll(id);
    setMessage("Enrichissement global terminé.");
    await load();
  });

  const handleDelete = () => withAction("delete", async () => {
    await apiClient.adminProspectDelete(id);
    navigate("/admin/prospects");
  });

  const handleArchive = () => withAction("archive", async () => {
    await apiClient.adminProspectArchive(id);
    setMessage("Archivé.");
    await load();
  });

  const convertToLead = () => withAction("convert", async () => {
    const data = await apiClient.adminProspectConvertToLead(id);
    setMessage(`Converti en lead: ${data?.lead?.email || "ok"}`);
    await load();
  });

  if (!prospect && errorMessage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/prospects")}>Retour au pipeline</Button>
      </Box>
    );
  }
  if (!prospect) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 4 }}>
        <Typography>Chargement du prospect...</Typography>
      </Box>
    );
  }

  const positives = (prospect.signals || []).filter((s) => s.category === "reason_positive");
  const missing = (prospect.signals || []).filter((s) => s.category === "reason_missing");
  const webSignals = (prospect.signals || []).filter((s) => s.signal_type === "website_scan");
  const otherSignals = (prospect.signals || []).filter(
    (s) => s.category !== "reason_positive" && s.category !== "reason_missing" && s.signal_type !== "website_scan"
  );
  const sourceTypes = [...new Set((prospect.sources || []).map((s) => s.source_type))];
  const busy = Boolean(busyAction);
  const pc = prospect.contacts?.[0];

  return (
    <>
      <SEO title={`${prospect.company_name} | DigiBuddy`} noIndex noFollow />

      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Box>
          <Typography variant="h5">{prospect.company_name}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              {prospect.industry || "Secteur inconnu"} · {prospect.location_label || "Lieu inconnu"}
            </Typography>
            <Typography variant="caption" color="text.secondary">— Sources :</Typography>
            {sourceTypes.map((t) => <SourceBadge key={t} type={t} />)}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Tooltip title="Retour">
            <IconButton size="small" onClick={() => navigate(-1)} aria-label="Retour"><ArrowBackIcon /></IconButton>
          </Tooltip>
          <Button size="small" color="warning" onClick={handleArchive} disabled={busy}>Archiver</Button>
          <Button size="small" color="error" onClick={() => setConfirmDelete(true)} disabled={busy}>Supprimer</Button>
        </Box>
      </Box>

      {message && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMessage("")}>{message}</Alert>}
      {errorMessage && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErrorMessage("")}>{errorMessage}</Alert>}

      {/* BARRE DE STATUT — cliquer pour changer */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>Statut :</Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", flex: 1 }}>
            {STATUS_FLOW.map((step, idx) => {
              const isCurrent = prospect.qualification_status === step.key;
              const currentIdx = STATUS_FLOW.findIndex((s) => s.key === prospect.qualification_status);
              const isPast = idx < currentIdx && currentIdx >= 0;
              return (
                <Chip
                  key={step.key}
                  label={step.label}
                  size="small"
                  clickable
                  disabled={busy}
                  onClick={() => {
                    if (!isCurrent) {
                      withAction("status", async () => {
                        await apiClient.adminProspectPatch(id, { qualification_status: step.key });
                        setMessage(`Statut changé → ${step.label}`);
                        await load();
                      });
                    }
                  }}
                  sx={{
                    fontWeight: isCurrent ? 700 : 400,
                    bgcolor: isCurrent ? step.color : isPast ? `${step.color}40` : "grey.100",
                    color: isCurrent ? "#fff" : "text.primary",
                    border: isCurrent ? `2px solid ${step.color}` : "1px solid transparent",
                    "&:hover": { bgcolor: `${step.color}80`, color: "#fff" },
                    transition: "all 0.15s",
                  }}
                />
              );
            })}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          <Chip label={`Score ${prospect.global_score}/100`} color={prospect.global_score >= 60 ? "success" : prospect.global_score >= 35 ? "warning" : "default"} size="small" />
          <Chip label={getPriorityLabel(prospect.priority_level)} variant="outlined" size="small" />
          <Chip label={getApproachabilityLabel(prospect.approachability_label)} variant="outlined" size="small" />
          <Chip label={`Complétude ${prospect.data_completeness_score}%`} variant="outlined" size="small" />
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* COLONNE GAUCHE : données clés + contact */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Données clés</Typography>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Site web</Typography>
              <Typography variant="body2">
                {prospect.website_url ? (
                  <a href={prospect.website_url} target="_blank" rel="noopener noreferrer">
                    {prospect.website_domain || prospect.website_url}
                  </a>
                ) : <span style={{ color: "red" }}>Aucun site trouvé</span>}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Email principal</Typography>
              <Typography variant="body2" color={pc?.email ? "text.primary" : "error.main"}>
                {pc?.email || "Non trouvé"}
                {pc?.source && <SourceBadge type={pc.source} />}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Téléphone</Typography>
              <Typography variant="body2" color={pc?.phone ? "text.primary" : "error.main"}>
                {pc?.phone || "Non trouvé"}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Contact</Typography>
              <Typography variant="body2">
                {pc?.full_name || "Décideur non identifié"} {pc?.role ? `(${pc.role})` : ""}
              </Typography>
            </Box>
            {prospect.linkedin_company_url && (
              <Box>
                <Typography variant="caption" color="text.secondary">LinkedIn</Typography>
                <Typography variant="body2">
                  <a href={prospect.linkedin_company_url} target="_blank" rel="noopener noreferrer">Profil</a>
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "primary.main" }}>
              Actions recommandées
            </Typography>
            <RecommendedActions prospect={prospect} />
          </Paper>
        </Grid>

        {/* COLONNE DROITE : score détaillé */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Score détaillé</Typography>
            <ScoreBar label="Besoin digital" value={prospect.fit_score} max={30} tip="Pas de site = 25pts, CMS cheap (Wix...) = 18pts, mauvais SEO/formulaire/HTTPS = +3-5pts chacun. Site bien fait = score bas." />
            <ScoreBar label="Budget / CA" value={prospect.economic_score} max={25} tip="Nb avis Google (activité), note, données Pappers (CA, effectifs)" />
            <ScoreBar label="Contactabilité" value={prospect.reachability_score} max={20} tip="Email pro (8pts), téléphone (5pts), décideur (4pts), page contact (1pt)" />
            <ScoreBar label="Opportunité" value={prospect.problem_score} max={15} tip="Pas de site = 10pts, CMS cheap = 8pts, SEO/formulaire manquant = 3-6pts, angle identifié = +3pts" />
            <ScoreBar label="Données" value={prospect.growth_score} max={10} tip="Nb de sources, audit web fait, données récentes" />
          </Paper>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "success.main" }}>
                  Forces ({positives.length})
                </Typography>
                {positives.length === 0 && <Typography variant="caption" display="block">-</Typography>}
                {positives.map((s) => (
                  <Typography key={s.id} variant="caption" display="block">+ {s.label}</Typography>
                ))}
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "error.main" }}>
                  Manques ({missing.length})
                </Typography>
                {missing.length === 0 && <Typography variant="caption" display="block">-</Typography>}
                {missing.map((s) => (
                  <Typography key={s.id} variant="caption" display="block">- {s.label}</Typography>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* AUDIT WEB */}
      {webSignals.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Audit web ({webSignals.length} signaux)</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {webSignals.map((s) => (
              <Chip
                key={s.id}
                size="small"
                variant="outlined"
                label={s.label}
                color={s.weight > 0 ? "success" : s.weight < 0 ? "error" : "default"}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* ENRICHISSEMENT */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Enrichir les données</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {ENRICH_SOURCES.map((src) => (
            <Button key={src.key} size="small" variant="outlined" disabled={busy} onClick={() => runEnrichment(src.key)}>
              {busyAction === src.key ? "..." : src.label}
            </Button>
          ))}
          <Button size="small" variant="contained" disabled={busy} onClick={enrichAll}>
            {busyAction === "enrich_all" ? "..." : "Tout enrichir"}
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={recalculate}>
            {busyAction === "recalculate" ? "..." : "Recalculer score"}
          </Button>
        </Box>
      </Paper>

      {/* MODIFIER LA FICHE */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Informations complémentaires</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Canal recommandé" value={patch.recommended_channel}
              onChange={(e) => setPatch({ ...patch, recommended_channel: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Problème principal" value={patch.primary_problem}
              onChange={(e) => setPatch({ ...patch, primary_problem: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Angle commercial" value={patch.primary_angle}
              onChange={(e) => setPatch({ ...patch, primary_angle: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Motif exclusion" value={patch.exclusion_reason}
              onChange={(e) => setPatch({ ...patch, exclusion_reason: e.target.value })} />
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button variant="contained" onClick={save} disabled={busy}>
            {busyAction === "save" ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
          {pc?.email && (
            <Button variant="outlined" color="success" onClick={convertToLead} disabled={busy}>
              {busyAction === "convert" ? "..." : "Convertir en lead"}
            </Button>
          )}
          {!pc?.email && (
            <Tooltip title="Email requis pour convertir en lead">
              <span>
                <Button variant="outlined" color="success" disabled>Convertir en lead</Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* AUTRES SIGNAUX */}
      {otherSignals.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Signaux enrichissement ({otherSignals.length})</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Signal</TableCell>
                <TableCell align="right">Poids</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {otherSignals.slice(0, 20).map((s) => (
                <TableRow key={s.id}>
                  <TableCell><SourceBadge type={s.signal_type} /></TableCell>
                  <TableCell><Typography variant="caption">{s.category}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{s.label}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color={s.weight > 0 ? "success.main" : s.weight < 0 ? "error.main" : "text.secondary"}>
                      {s.weight > 0 ? "+" : ""}{s.weight}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* CONTACTS */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Contacts ({prospect.contacts?.length || 0})</Typography>
        {(prospect.contacts || []).map((c) => (
          <Box key={c.id} sx={{ mb: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2">
                {c.full_name || "Inconnu"} {c.role ? `(${c.role})` : ""}
              </Typography>
              {c.is_primary && <Chip size="small" label="principal" color="primary" />}
              {c.source && <SourceBadge type={c.source} />}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {c.email || "pas d'email"} · {c.phone || "pas de tél"}
            </Typography>
          </Box>
        ))}
        {(prospect.contacts || []).length === 0 && (
          <Typography variant="caption" color="text.secondary">Aucun contact trouvé</Typography>
        )}
      </Paper>

      {/* HISTORIQUE */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Historique ({prospect.events?.length || 0})</Typography>
        {(prospect.events || []).length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Détails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prospect.events.slice(0, 30).map((evt) => (
                <TableRow key={evt.id}>
                  <TableCell><Typography variant="caption">{new Date(evt.created_at).toLocaleString("fr-FR")}</Typography></TableCell>
                  <TableCell><Chip size="small" label={evt.type} variant="outlined" /></TableCell>
                  <TableCell><Typography variant="caption">{evt.label}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ maxWidth: 220, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {evt.payload ? JSON.stringify(evt.payload) : "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="caption" color="text.secondary">Aucun événement</Typography>
        )}
      </Paper>

      {/* DIALOG SUPPRESSION */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Supprimer ce prospect ?</DialogTitle>
        <DialogContent>
          <Typography>
            {prospect.company_name} sera supprimé définitivement avec tous ses contacts, signaux et historique.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
